import { Button, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useAccess } from '../../auth/useAccess.js';
import { downloadCredentialed } from '../../lib/download.js';
import { BulkActionBar } from '../_ops/BulkActionBar.js';
import { AddEntry } from './AddEntry.js';
import type { TimesheetsWeekResponse } from './contractsMirror.js';
import { EditRequestReview } from './EditRequestReview.js';
import { weeklyHoursPdfUrl } from './exportLinks.js';
import { OnClockRoster } from './OnClockRoster.js';
import { PunchTiles } from './PunchTiles.js';
import { StartShiftFor } from './StartShiftFor.js';
import type { TimesheetsSearch } from './searchParams.js';
import {
  useBulkApproveWeeksMutation,
  useBulkReopenWeeksMutation,
  useReopenWeekMutation,
} from './useApprovalMutation.js';
import { WeekGrid } from './WeekGrid.js';

/**
 * The faithful 1:1 Timesheets port (UI-SPEC archetype D + bulk) on the locked Velocity brand — the
 * heaviest screen. Page wrapper + header (title, subtitle, the weekly-hours PDF link) + the week
 * prev/next nav strip + the bulk-approval bar (when ≥1 week is selected) + the editable WeekGrid + the
 * edit-request (time-correction) review + active-clock indicators.
 *
 * The coarse endpoint (22-13) owns ALL aggregation — the per-employee grid, the running totals, the
 * pending edit-request queue, the active clocks. This view only renders the returned payload.
 *
 * THE GUARDRAIL (D-03 / T-22-payroll): every hours value is SERVER-COMPUTED + lunch-adjusted; this
 * view performs NO lunch / overtime arithmetic. The bulk "Approve N weeks" CTA is the only accent
 * surface in the action bar; every color is a locked `@hbs-us/ui` token (no hex, BRAND-06).
 */

/** Add `days` days to a Monday ISO week-start (UTC-stable), returning the new ISO date. */
function shiftWeek(weekStart: string, days: number): string {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface TimesheetsViewProps {
  data: TimesheetsWeekResponse;
  search: TimesheetsSearch;
}

export function TimesheetsView({ data }: TimesheetsViewProps) {
  const { t } = useTranslation();
  const bulkApprove = useBulkApproveWeeksMutation();
  const bulkReopen = useBulkReopenWeeksMutation();
  const reopenWeek = useReopenWeekMutation();
  // Reaching this page needs `time.view`; every APPROVAL surface on it needs
  // `time.approve` on the server, so the two must not be conflated. Hiding these is
  // UX only — approve-week, reopen-week, the PDF and resolve-open all re-check.
  const canApprove = useAccess()({ permission: 'time.approve' });

  const weekStart = data.weekStart ?? '';
  const dates = data.dates ?? [];
  const rows = data.rows ?? [];
  const pendingRequests = data.pendingRequests ?? [];
  const activeClocks = data.activeClocks ?? [];
  const totalHours = Number(data.summary?.totalHours ?? 0);
  const employeeCount = Number(data.summary?.employeeCount ?? rows.length);
  const approvedCount = Number(data.summary?.approvedCount ?? 0);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const selectedRows = rows.filter((row) => selectedIds.has(row.employeeId));
  const selectedPendingRows = selectedRows.filter((row) => !row.isApprovedWeek);
  const selectedApprovedRows = selectedRows.filter((row) => row.isApprovedWeek);

  const toggleRow = (employeeId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const allSelected = rows.length > 0 && rows.every((r) => prev.has(r.employeeId));
      return allSelected ? new Set() : new Set(rows.map((r) => r.employeeId));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const approveSelected = () => {
    const weeks = selectedPendingRows.map((row) => ({ employeeId: row.employeeId, weekStart }));
    if (weeks.length === 0) return;
    bulkApprove.mutate({ weeks }, { onSuccess: clearSelection });
  };

  const reopenSelected = () => {
    const weeks = selectedApprovedRows.map((row) => ({ employeeId: row.employeeId, weekStart }));
    if (weeks.length === 0) return;
    bulkReopen.mutate({ weeks }, { onSuccess: clearSelection });
  };

  const reopenEmployeeWeek = (employeeId: number) => {
    if (!weekStart) return;
    reopenWeek.mutate({ employeeId, weekStart });
  };

  const prevWeek = weekStart ? shiftWeek(weekStart, -7) : undefined;
  const nextWeek = weekStart ? shiftWeek(weekStart, 7) : undefined;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('timesheets:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('timesheets:subtitle')}</p>
        </div>
        {/* `GET /timesheets/weekly-hours/pdf` is guarded by `time.approve`, so without it
            this button only ever produced a failed download. */}
        {weekStart && canApprove ? (
          <Button
            variant="secondary"
            onClick={() =>
              void downloadCredentialed(
                weeklyHoursPdfUrl(weekStart),
                `weekly-hours-${weekStart}.pdf`,
              ).catch((err) => console.error('weekly hours pdf download failed', err))
            }
          >
            {t('timesheets:actions.weeklyPdf')}
          </Button>
        ) : null}
      </header>

      <PunchTiles />

      <section
        aria-label={t('timesheets:summary.regionLabel')}
        className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3"
      >
        <SummaryCard
          label={t('timesheets:summary.totalHours')}
          value={totalHours.toFixed(2)}
          mono
        />
        <SummaryCard label={t('timesheets:summary.employees')} value={String(employeeCount)} />
        <SummaryCard label={t('timesheets:summary.approved')} value={String(approvedCount)} />
      </section>

      <nav
        aria-label={t('timesheets:nav.regionLabel')}
        className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
      >
        <div className="flex items-center gap-2">
          {prevWeek ? (
            <Button asChild variant="secondary" size="sm">
              <Link to="/timesheets" search={{ week: prevWeek }}>
                {t('timesheets:nav.prevWeek')}
              </Link>
            </Button>
          ) : null}
          {nextWeek ? (
            <Button asChild variant="secondary" size="sm">
              <Link to="/timesheets" search={{ week: nextWeek }}>
                {t('timesheets:nav.nextWeek')}
              </Link>
            </Button>
          ) : null}
        </div>
        <p className="font-mono text-muted-foreground text-sm">
          {weekStart} – {dates[6] ?? ''}
        </p>
      </nav>

      <OnClockRoster clocks={activeClocks} />

      {/* Starting someone else's shift is an approver action; the endpoint re-checks it. */}
      {canApprove ? <StartShiftFor rows={rows} /> : null}

      <AddEntry rows={rows} dates={dates} />

      {canApprove && selectedIds.size > 0 ? (
        <div className="mb-4">
          <BulkActionBar
            selectedCount={selectedIds.size}
            summaryLabel={t('timesheets:bulk.selected', { count: selectedIds.size })}
            clearLabel={t('timesheets:bulk.clear')}
            onClear={clearSelection}
            actions={[
              ...(selectedPendingRows.length > 0
                ? [
                    {
                      key: 'approve-weeks',
                      label: t('timesheets:bulk.approveWeeks', {
                        count: selectedPendingRows.length,
                      }),
                      onClick: approveSelected,
                      variant: 'primary' as const,
                      disabled: bulkApprove.isPending,
                    },
                  ]
                : []),
              ...(selectedApprovedRows.length > 0
                ? [
                    {
                      key: 'reopen-weeks',
                      label: t('timesheets:bulk.reopenWeeks', {
                        count: selectedApprovedRows.length,
                      }),
                      onClick: reopenSelected,
                      variant: 'secondary' as const,
                      disabled: bulkReopen.isPending,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="mb-6">
          <WeekGrid
            dates={dates}
            rows={rows}
            weekStart={weekStart}
            selectedIds={selectedIds}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
            onReopenWeek={reopenEmployeeWeek}
            reopeningWeek={reopenWeek.isPending}
            canApprove={canApprove}
          />
        </div>
      ) : (
        <div className="mb-6 rounded-md border border-border bg-card p-16 text-center">
          <h2 className="font-display font-semibold text-foreground text-xl">
            {t('timesheets:empty.heading')}
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-muted-foreground text-sm">
            {t('timesheets:empty.body')}
          </p>
        </div>
      )}

      {/* The whole review queue is an approver surface: listing it, approving and
          rejecting are all `time.approve` on the server. Gated at the call site so
          EditRequestReview stays a dumb renderer. */}
      {canApprove ? <EditRequestReview requests={pendingRequests} /> : null}
    </div>
  );
}

function SummaryCard({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-card p-6">
      <p className="text-muted-foreground text-sm">{label}</p>
      <p
        className={
          mono
            ? 'mt-1 font-display font-mono font-semibold text-2xl text-foreground'
            : 'mt-1 font-display font-semibold text-2xl text-foreground'
        }
      >
        {value}
      </p>
    </div>
  );
}
